/* Browser adapter: localized Shopify endpoints, country inventory and accessible gift UI. */
(function () {
  'use strict';
  const C = window.PMGiftCore;
  if (!C || !window.PMGiftController || window.PMGifts) return;
  const initial = document.querySelector('[data-gift-config]');
  if (!initial) return;
  let config;
  try { config = JSON.parse(initial.textContent); } catch (_) { return; }
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const events = ['on:cart:add', 'on:line-item:change', 'cart:updated', 'cart:change', 'shopify:cart:updated', 'volume-discount:cart-state', 'on:bfcache:load-restore'];
  let lastState, timer, priceWaitTimer, checkoutPending = false, checkoutBypass = false, refreshingCart = false, pendingCart = '';
  const followUps = new Set();
  // Keep our reads/writes outside the observer to prevent recursive cart requests.
  const cartFetch = window.fetch.bind(window);
  function text(key, values = {}) {
    let message = config.strings?.[key] || key;
    for (const [name, value] of Object.entries(values)) message = message.replaceAll('[' + name + ']', String(value));
    return message;
  }
  async function request(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await cartFetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}) });
      if (!response.ok) { const error = new Error('Gift request failed'); error.status = response.status; throw error; }
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  async function getConfig(transactionCart) {
    const parse = html => {
      if (typeof html !== 'string') return null;
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gift-config]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_) { return null; }
    };
    // The current change response can carry fresh market/config data alongside
    // the cart HTML. This avoids a second request without caching across edits.
    const bundled = parse(transactionCart?.sections?.['gift-promotion-state']);
    if (bundled) { config = bundled; return config; }
    const data = await request(root + '?sections=gift-promotion-state');
    const fresh = parse(data['gift-promotion-state']);
    if (!fresh) throw new Error('Gift settings unavailable');
    config = fresh; return config;
  }
  async function canAdd(candidate, cart, current, additional = 1) {
    // Same country-context inventory strategy used by main-bulk-purchase.
    // A temporary Storefront cart validates stock without changing the buyer's Ajax cart.
    const quantity = cart.items.filter(i => C.variantOf(i) === String(candidate.id)).reduce((n, i) => n + i.quantity, 0) + additional;
    const result = await request('/api/2026-07/graphql.json', {
      query: 'mutation GiftInventory($input: CartInput!, $country: CountryCode!) @inContext(country: $country) { cartCreate(input: $input) { cart { lines(first: 10) { nodes { quantity merchandise { ... on ProductVariant { id } } } } } userErrors { code message } } }',
      variables: { country: current.country, input: { buyerIdentity: { countryCode: current.country }, lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + candidate.id, quantity }] } }
    });
    if (result.errors?.length || !result.data?.cartCreate) throw new Error('Country inventory unavailable');
    const payload = result.data.cartCreate;
    if (payload.userErrors?.length) {
      if (payload.userErrors.every(e => /STOCK|NOT_FOUND|NOT_AVAILABLE|INVALID_MERCHANDISE/i.test(e.code || ''))) return false;
      throw new Error('Country inventory unavailable');
    }
    if (!payload.cart?.lines?.nodes) throw new Error('Country inventory unavailable');
    return payload.cart.lines.nodes.filter(i => i.merchandise?.id === 'gid://shopify/ProductVariant/' + candidate.id)
      .reduce((n, i) => n + i.quantity, 0) >= quantity;
  }
  function announce(message, kind = 'gift') {
    let node = document.getElementById('gift-promotion-notice');
    if (!node) {
      node = document.createElement('div'); node.id = 'gift-promotion-notice'; node.className = 'gift-promotion-notice';
      node.setAttribute('role', 'alert');
      const copy = document.createElement('span'); node.append(copy);
      const close = document.createElement('button'); close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', text('close'));
      close.addEventListener('click', () => { node.hidden = true; }); node.append(close); document.body.append(node);
    }
    node.querySelector('span').textContent = message; node.dataset.kind = kind; node.hidden = false;
  }
  const manager = new window.PMGiftController({
    exclusive: task => window.PMCartOperations.run(task),
    getConfig, getCart: () => request(root + 'cart.js'),
    change: (id, quantity, item) => window.PMCartOperations.change(item, quantity),
    add: (id, properties) => request(root + 'cart/add.js', { items: [{ id, quantity: 1, properties }] }),
    addMany: items => request(root + 'cart/add.js', { items }),
    canAdd,
    load: key => JSON.parse(localStorage.getItem(key) || 'null'),
    save: (key, value) => { const serialized = JSON.stringify(value); if (localStorage.getItem(key) !== serialized) localStorage.setItem(key, serialized); },
    onState: state => {
      if (state.status === 'pending' && pendingCart !== C.fingerprint(state.cart, state.config, state.rule)) {
        pendingCart = C.fingerprint(state.cart, state.config, state.rule); sync(true, false);
      }
      clearTimeout(priceWaitTimer);
      if (state.status === 'pending') priceWaitTimer = setTimeout(() => {
        if (lastState?.status === 'pending') manager.emit(lastState.cart, lastState.config, lastState.rule);
      }, Math.max(1, 10001 - (Date.now() - (manager.priceWait?.since ?? Date.now()))));
      lastState = state; document.querySelectorAll('gift-promotion').forEach(node => node.render?.(state));
      document.dispatchEvent(new CustomEvent('gift-promotion:state', { detail: state }));
      if (!state.status) {
        pendingCart = '';
        const notice = document.getElementById('gift-promotion-notice');
        if (notice?.dataset.kind === 'gift') notice.hidden = true;
      }

    },
    onError: () => {
      if (lastState) { lastState = { ...lastState, status: 'error' }; document.querySelectorAll('gift-promotion').forEach(node => node.render?.(lastState)); }
      announce(text('error'), 'gift');
    },
    onCart: async (cart) => {
      refreshingCart = true;
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'pm-gwp' } }));
        const drawer = document.getElementById('cart-drawer');
        if (typeof drawer?.refresh === 'function') await drawer.refresh();
        else await document.querySelector('cart-items')?.refresh?.();
      } finally { refreshingCart = false; }
    }
  });
  manager.isFreeGift = item => C.confirmed(item, config);
  window.PMGifts = manager;
  function clearFollowUps() { for (const id of followUps) clearTimeout(id); followUps.clear(); }
  let invalidateQueued = false;
  function sync(settle = false, invalidate = true) {
    clearTimeout(timer);
    invalidateQueued = invalidateQueued || invalidate;
    timer = setTimeout(() => {
      const invalidateCurrentWork = invalidateQueued; invalidateQueued = false;
      manager.request(invalidateCurrentWork).catch(() => {});
    }, 120);
    if (settle) {
      clearFollowUps();
      // Regios/other cart integrations may return updated prices after the first response.
      for (const delay of [500, 1500, 4000]) {
        const id = setTimeout(() => { followUps.delete(id); sync(false, false); }, delay);
        followUps.add(id);
      }
    }
  }
  function cartRequest(input, method) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      if (url.origin !== location.origin || !/\/cart(?:\/(?:add|change|update|clear))?(?:\.js|\.json)?\/?$/.test(url.pathname)) return null;
      return { write: String(method || input.method || 'GET').toUpperCase() !== 'GET' };
    } catch (_) { return null; }
  }
  let lastObservedCart = '';
  function observeCartRead(cart) {
    if (refreshingCart || !Array.isArray(cart?.items) || !cart.currency) return;
    const signature = C.presentation(cart);
    if (lastState && signature !== C.presentation(lastState.cart) && signature !== lastObservedCart) sync();
    lastObservedCart = signature;
  }
  // Observe successful cart responses without consuming or changing their bodies.
  window.fetch = async function (input, options) {
    if (options?.[Symbol.for('pm.cart.operation')]) return cartFetch(input, options);
    const observed = cartRequest(input, options?.method);
    const response = await cartFetch(input, options);
    if (observed && response.ok && !refreshingCart) {
      if (observed.write) sync(true);
      else response.clone().json().then(observeCartRead).catch(() => {});
    }
    return response;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this._pmGiftRequest = cartRequest(url, method);
    if (!this._pmGiftObserved) {
      this._pmGiftObserved = true;
      this.addEventListener('loadend', () => {
        if (this._pmGiftRequest && this.status >= 200 && this.status < 300 && !refreshingCart) {
          if (this._pmGiftRequest.write) sync(true);
          else { try { observeCartRead(this.responseType === 'json' ? this.response : JSON.parse(this.responseText)); } catch (_) {} }
        }
      });
    }
    return xhrOpen.call(this, method, url, ...args);
  };
  for (const name of events) document.addEventListener(name, event => {
    if (!refreshingCart && event.detail?.source !== 'pm-gwp') {
      const sameCart = event.detail?.cart && lastState && C.presentation(event.detail.cart) === C.presentation(lastState.cart);
      sync(true, !sameCart);
    }
  });
  window.addEventListener('pageshow', () => sync(true));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) sync(true); });
  window.addEventListener('storage', event => { if (event.key?.startsWith('pm-gwp-v1')) { manager.memory.clear(); sync(true); } });
  // Recheck visible carts containing gifts even when an integration emits no event.
  setInterval(() => {
    if (!document.hidden && lastState && (lastState.cart.items.some(i => C.isGift(i, config)) ||
        Boolean(lastState.rule) !== Boolean(C.active(config) && C.resolveRule(config, config.currency)))) sync(false, false);
  }, 15000);
  window.addEventListener('pagehide', () => { clearTimeout(timer); clearTimeout(priceWaitTimer); clearFollowUps(); });

  // Explicit removal waives the selected automatic slots, so reconciliation
  // does not immediately add the charged/pending gift back again.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-gift-remove-slots]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    try { await manager.waiveSlots(JSON.parse(button.dataset.giftRemoveSlots)); }
    catch (_) { announce(text('error')); }
    finally { if (button.isConnected) button.disabled = false; }
  });

  async function gate(event, resume) {
    if (checkoutBypass) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    try { await manager.beforeCheckout(); checkoutBypass = true; resume(); }
    catch (_) { announce(text('checkout_error'), 'checkout'); }
    finally { checkoutPending = false; setTimeout(() => { checkoutBypass = false; }, 0); }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && /\/(?:checkouts?|checkout\/.*)$/.test(url.pathname)) gate(event, () => location.assign(url.href));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, location.href);
    if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(action.pathname)) {
      const submitter = event.submitter;
      gate(event, () => {
        // Refreshing a drawer can replace both the form and its checkout button.
        const currentForm = form.isConnected ? form : document.getElementById(form.id);
        if (!(currentForm instanceof HTMLFormElement)) throw new Error('Cart form unavailable');
        const currentSubmitter = submitter?.isConnected && submitter.form === currentForm ? submitter :
          Array.from(currentForm.elements).find(input => input.name === submitter?.name && input.type === 'submit');
        if (submitter?.name === 'checkout' && !currentSubmitter) throw new Error('Checkout button unavailable');
        currentForm.requestSubmit(currentSubmitter);
      });
    }
  }, true);

  class GiftPromotion extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); const options = { signal: this.abort.signal };
      this.trigger = this.querySelector('.gift-promotion__trigger');
      this.panel = this.querySelector('.gift-promotion__details');
      this.open = false; this.previousCount = null;
      this.trigger.addEventListener('click', () => this.setOpen(!this.open), options);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.setOpen(true); }, options);
      this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && !this.contains(document.activeElement)) this.setOpen(false); }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.setOpen(false); }, options);
      document.addEventListener('click', event => { if (!this.contains(event.target)) this.setOpen(false); }, options);
      this.addEventListener('keydown', event => { if (event.key === 'Escape') { this.trigger.focus(); this.setOpen(false); } }, options);
      this.querySelector('[data-gift-restore]').addEventListener('click', () => manager.restore().catch(() => announce(text('error'))), options);
      this.querySelector('[data-gift-retry]').addEventListener('click', () => { pendingCart = ''; manager.retry().catch(() => announce(text('error'))); }, options);
      if (lastState) this.render(lastState);
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; clearTimeout(this.animationTimer); }
    setOpen(open) { this.open = open; this.panel.hidden = !open; this.trigger.setAttribute('aria-expanded', String(open)); }
    render(state) {
      this.hidden = !state.rule;
      this.closest('[data-bfcm-bar]')?.toggleAttribute('data-gift-visible', !this.hidden);
      if (this.hidden) { this.setOpen(false); this.previousCount = null; return; }
      const currency = state.rule.currency;
      // Shopify Ajax/Liquid money uses a hundredths representation, including JPY/KRW.
      const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(value / 100);
      const count = state.giftCount ?? state.gifts.length;
      const changed = this.previousCount !== null && count > this.previousCount;
      this.previousCount = count;
      // Keep a just-unlocked gift full until its free line has been confirmed.
      const addedCount = state.addedCount ?? count;
      const waitingForGift = state.entitled > addedCount + (state.waived || 0);
      const pending = state.status === 'syncing' && waitingForGift;
      const failed = ['stock', 'error', 'discount'].includes(state.status);
      this.classList.toggle('is-pending', pending);
      this.trigger.setAttribute('aria-busy', String(pending));
      const statusIcon = this.querySelector('[data-gift-status]');
      statusIcon.hidden = !pending && !failed;
      statusIcon.textContent = failed ? '!' : '';
      statusIcon.classList.toggle('is-error', failed);
      this.classList.toggle('is-capped', state.capped);
      this.targetRatio = waitingForGift ? 1 : state.ratio;
      if (!this.classList.contains('is-earned')) this.style.setProperty('--gift-progress', (this.targetRatio * 100) + '%');
      if (changed && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        clearTimeout(this.animationTimer);
        this.classList.remove('is-earned'); void this.offsetWidth; this.classList.add('is-earned');
        this.style.setProperty('--gift-progress', '100%');
        // 450ms color fill, followed by the 650ms celebration in gift-promotion.css.
        this.animationTimer = setTimeout(() => { this.classList.remove('is-earned'); this.style.setProperty('--gift-progress', (this.targetRatio * 100) + '%'); }, 1100);
      }
      this.querySelector('[data-gift-earned-text]').textContent = addedCount > count ? text('added_pending', { count: addedCount }) : text('earned', { count });
      this.querySelector('[data-gift-amount]').textContent = state.capped && !waitingForGift ? text('maximum') : money(waitingForGift ? state.rule.threshold : state.remainder) + ' / ' + money(state.rule.threshold);
      const message = state.status ? text(state.status) :
        state.capped ? text('maximum') : text('remaining', { amount: money(state.remaining), number: state.entitled + 1 });
      this.querySelector('[data-gift-message]').textContent = message;
      this.trigger.setAttribute('aria-label', text('earned', { count }) + '. ' + message);
      this.querySelector('[data-gift-restore]').hidden = !state.waived;
      this.querySelector('[data-gift-retry]').hidden = !['stock', 'error', 'discount'].includes(state.status);
    }
  }
  if (!customElements.get('gift-promotion')) customElements.define('gift-promotion', GiftPromotion);
  sync();
})();
